import { useApolloClient } from '@apollo/client';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { useCartActions, useCartValues } from '@fhs/cart/src/state/cart-store';
import { useCartStoreHydration } from '@fhs/cart/src/state/cart-store/cart-store-values';
import useCartTotal from '@fhs/cart/src/state/cart-store/pricing-cart/use-cart-total';
import { isCartExpired } from '@fhs/cart/src/utils';
import { queryClient } from '@fhs/client';
import {
  evaluateChallengesQueryKeys,
  loyaltyIncentivesQueryKeys,
  rewardsTiersQueryKeys,
  usePrefetchEvaluateChallenges,
  useRewardTiers,
} from '@fhs/loyalty/src/queries/loyalty.queries';
import { useChallengesUiHelper } from '@fhs/loyalty/src/state/challenges-ui-helper-state';
import { usePathnameRef } from '@fhs/utils';
import { useLoyaltyUserTransactionsQuery } from 'generated/graphql-gateway';
import {
  GetOrderDocument,
  RbiOrderStatus,
  useGetUserOrdersLazyQuery,
  useUpdateOrderMutation,
} from 'generated/rbi-graphql';
import { getPosVendorFromStore } from 'hooks/menu/use-pos-vendor';
import useDebouncedEffect from 'hooks/use-debounce-effect';
import useDialogModal from 'hooks/use-dialog-modal';
import useEffectOnUpdates from 'hooks/use-effect-on-updates';
import useEffectOnce from 'hooks/use-effect-once';
import useInterval from 'hooks/use-interval';
import { useLocalStorageState } from 'hooks/use-local-storage-state';
import { MAX_TIPS_AMOUNT_IN_CENTS } from 'pages/cart/your-cart/totals/use-pickup-tips';
import { useAuthContext } from 'state/auth';
import { CustomEventNames, EventTypes, useCRMEventsContext } from 'state/crm-events';
import { useErrorContext } from 'state/errors';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { useLoyaltyContext } from 'state/loyalty';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';
import { getCmsOffersMapByCmsId } from 'state/loyalty/utils';
import { useOffersContext } from 'state/offers';
import { useCartTip } from 'state/order/hooks/use-cart-tip';
import { usePaymentContext } from 'state/payment';
import { useServiceModeContext } from 'state/service-mode';
import { ServiceMode } from 'state/service-mode/types';
import { useStoreContext } from 'state/store';
import { useUIContext } from 'state/ui';
import * as DatadogLogger from 'utils/datadog';
import { ISOs, getCountryAndCurrencyCodes } from 'utils/form/constants';
import { useFlag } from 'utils/launchdarkly';
import LocalStorage, { StorageKeys } from 'utils/local-storage';
import logger, { addLoggingContext } from 'utils/logger';
import { routes } from 'utils/routing';
import { isCatering as isCateringOrder } from 'utils/service-mode';

import { TipAmounts } from './constants';
import useAlertOrderCateringMinimum from './hooks/use-alert-order-catering-min';
import useAlertOrderLimit from './hooks/use-alert-order-limit';
import { useHandleReorder } from './hooks/use-handle-reorder';
import useRewardDiscount from './hooks/use-reward-discount';
import { useUnavailableCartEntries } from './hooks/use-unavailable-cart-entries';
import { preloadedOrder } from './preloaded-order';
import { OrderStatus } from './types';
import { determineLimit, validateCartEntries } from './utils';

export { OrderStatus, ServiceMode, TipAmounts };
export const OrderContext = React.createContext();
export const useOrderContext = () => useContext(OrderContext);

const debounceTime = 250;

// Separate component so the prefetch inputs don't re-render the whole order provider
function EvaluateChallengesPrefetch() {
  usePrefetchEvaluateChallenges();
  return null;
}

export function OrderProvider(props) {
  const hasHydratedCartStore = useCartStoreHydration();
  useRewardTiers();

  const CART_VERSION = 1;

  const tipPercentThresholdCents = useFlag('tip-percent-threshold-cents') || 0;
  const { setShouldRefetchChallengeProgressUi } = useChallengesUiHelper();

  const isExplicitDonation = useFlag('explicit-donation');
  const [selectedDonationId, setSelectedDonationId] = useState(isExplicitDonation ? '' : '0');

  const [addItemToCartLoading, setAddItemToCartLoading] = useState(false);
  const { formatMessage } = useIntl();
  const apolloClient = useApolloClient();
  const auth = useAuthContext();
  const billingCountry = auth?.user?.details?.isoCountryCode || ISOs.USA;
  const { currencyCode } = getCountryAndCurrencyCodes(billingCountry);
  const { formatCurrencyForLocale } = useUIContext();
  const { logRBIEvent, logPurchase } = useCRMEventsContext();

  const { getPaymentMethods } = usePaymentContext();
  const {
    tipAmount,
    setTipAmount,
    tipSelection,
    setTipSelection,
    updateTipAmount,
    shouldShowTipPercentage,
  } = useCartTip();

  const {
    selectStore: selectNewStore,
    store,
    resetLastTimeStoreUpdated,
    isStoreAvailable,
    updateUserStoreWithCallback,
  } = useStoreContext();
  const { serviceMode, setServiceMode } = useServiceModeContext();
  const { getCurrentPathname } = usePathnameRef();
  const offers = useOffersContext();
  const { onConfirmRedemption, selectedOffer, isSelectedOfferCartEntry, selectedOfferCartEntry } =
    offers;
  const { setCurrentOrderId } = useErrorContext();
  const { refetchLoyaltyUser, evaluateEligibleItems } = useLoyaltyContext();

  const { loyaltyUser } = useLoyaltyUser();

  const dispatch = useAppDispatch();
  const appliedOffers = useAppSelector(selectors.loyalty.selectAppliedOffers);
  const loyaltyCmsOffers = useAppSelector(selectors.loyalty.selectCmsOffers);
  const incentivesIds = useAppSelector(selectors.loyalty.selectIncentivesIds);

  const loyaltyUserId = loyaltyUser?.id;
  const appliedLoyaltyOffers = useAppSelector(selectors.loyalty.selectAppliedOffers);
  const selectedOfferSelections = useAppSelector(selectors.loyalty.selectSelectedOfferSelections);
  const appliedRewards = useAppSelector(selectors.loyalty.selectAppliedLoyaltyRewards);

  const { refetch: refetchLoyaltyUserTransaction, data } = useLoyaltyUserTransactionsQuery({
    skip: !loyaltyUserId || auth.isAuthenticating,
    variables: { loyaltyId: loyaltyUserId || '' },
  });

  const [refetchGetUserOrders] = useGetUserOrdersLazyQuery({
    fetchPolicy: 'network-only',
    variables: {
      limit: 1,
      orderStatuses: [RbiOrderStatus.INSERT_SUCCESSFUL, RbiOrderStatus.UPDATE_SUCCESSFUL],
    },
  });

  const [executeUpdateOrderMutation] = useUpdateOrderMutation();

  const { cartEntries } = useCartValues();
  const {
    addItemToCart,
    removeItemFromCart: removeFromCart,
    removeAllItemsFromCart: removeAllFromCart,
    updateCartEntry,
    upsertCart,
    updateCartItemsQuantity: updateQuantity,
    emptyCart,
    isCartEntryInSwaps,
  } = useCartActions();

  const preloaded = useMemo(() => preloadedOrder(), []);

  useEffect(() => {
    if (data?.loyaltyUserV2?.transactions) {
      dispatch(actions.loyalty.setUserTransactions(data.loyaltyUserV2.transactions));
    }
  }, [data?.loyaltyUserV2?.transactions]);

  const [pendingReorder, setPendingReorder] = useState(null);
  const [reordering, setReordering] = useState(false);
  const [reorderedOrderId, setReorderedOrderId] = useState(null);

  const [cartIdEditing, setCartIdEditing] = useState(preloaded.cartIdEditing || '');

  const [curbsidePickupOrderId, setCurbsidePickupOrderId] = useState(
    preloaded.curbsidePickupOrderId || ''
  );
  const [curbsidePickupOrderTimePlaced, setCurbsidePickupOrderTimePlaced] = useState(
    preloaded.curbsidePickupOrderTimePlaced || ''
  );
  const [serverOrder, setServerOrder] = useState({});
  const [quoteId, setQuoteId] = useState(preloaded.quoteId || '');
  const [cateringPickupDateTime, setOrderCateringPickupDateTime] = useState(
    preloaded.cateringPickupDateTime || ''
  );
  const [deliveryAddress, setDeliveryAddress] = useState(preloaded.deliveryAddress || {});
  const [deliveryInstructions, setDeliveryInstructions] = useState(
    preloaded.deliveryInstructions || ''
  );

  const [fetchingPosData, setFetchingPosData] = useState(false);

  const [orderPhoneNumber, setOrderPhoneNumber] = useState(() => preloaded.orderPhoneNumber || '');

  React.useEffect(() => {
    if (orderPhoneNumber) {
      return;
    }
    if (auth?.user?.details?.phoneNumber) {
      setOrderPhoneNumber(auth?.user?.details?.phoneNumber);
    }
  }, [auth, orderPhoneNumber]);

  const [fireOrderIn, setFireOrderInRaw] = useState(0);
  // Anchor timestamp captured the moment fireOrderIn was set, so we can derive the
  // absolute target time (anchorMs + fireOrderIn * 1000) and recompute a fresh
  // fireOrderIn at commit time. Prevents drift when the x-user-datetime header
  // (set at request-send time) is later than the slot-selection time. See FHSG-433.
  const fireOrderInAnchorMsRef = useRef(Date.now());
  const setFireOrderIn = useCallback(seconds => {
    fireOrderInAnchorMsRef.current = Date.now();
    setFireOrderInRaw(seconds);
  }, []);
  const getCurrentFireOrderIn = useCallback(() => {
    const target = fireOrderInAnchorMsRef.current + fireOrderIn * 1000;
    return Math.max(0, Math.round((target - Date.now()) / 1000));
  }, [fireOrderIn]);
  const [cartPriceLimitExceeded, setCartPriceLimitExceeded] = useState(false);

  const [cartCateringPriceTooLow, setCartCateringPriceTooLow] = useState(false);

  const isDelivery = serviceMode === ServiceMode.DELIVERY;

  const { unavailableCartEntries, setUnavailableCartEntries } = useUnavailableCartEntries({
    serverOrder,
    cartEntries,
  });
  const [pendingRecentItem, setPendingRecentItem] = useState();

  // th specific: reward information
  const { cartHasRewardEligibleItem } = useRewardDiscount({
    serverOrder,
    cartEntries,
  });

  const updateShouldSaveDeliveryAddress = useCallback(shouldSaveDeliveryAddress => {
    setDeliveryAddress(previousAddress => ({
      ...previousAddress,
      shouldSave: shouldSaveDeliveryAddress,
    }));
  }, []);

  const orderLimitMessage = (maxLimit, maxCateringLimit, isOrderCatering) => {
    const limit = determineLimit({ maxLimit, maxCateringLimit, isCatering: isOrderCatering });
    return formatMessage(
      { id: 'orderLimitMessage' },
      {
        maxLimit: formatCurrencyForLocale(limit),
      }
    );
  };

  const [isDonationZeroAmountSelected, setIsDonationZeroAmountSelected] = useState(false);

  // Pick Up Tips
  const [selectedPickupTip, setSelectedPickupTip] = useLocalStorageState({
    key: StorageKeys.PICK_UP_TIPS_SELECTED,
    defaultReturnValue: '0',
  });
  // Pick Up Tips in case user selected "Other" option
  const [otherPickupTip, setOtherPickupTip] = useLocalStorageState({
    key: StorageKeys.OTHER_PICK_UP_TIPS_SELECTED,
    defaultReturnValue: undefined,
  });
  const pickUpTipsCents =
    selectedPickupTip === 'other' ? Number(otherPickupTip) : Number(selectedPickupTip);
  const pickUpTipsInputError =
    pickUpTipsCents > MAX_TIPS_AMOUNT_IN_CENTS
      ? `Maximum tip allowed is ${formatCurrencyForLocale(MAX_TIPS_AMOUNT_IN_CENTS)}`
      : undefined;

  const orderEmptyCart = useCallback(
    () =>
      emptyCart(setCartIdEditing, isDonationZeroAmountSelected, setIsDonationZeroAmountSelected),
    [emptyCart, setCartIdEditing, isDonationZeroAmountSelected, setIsDonationZeroAmountSelected]
  );

  useEffectOnce(() => {
    if (isCartExpired(preloaded?.timestamp)) {
      orderEmptyCart();
    }
  });

  // Periodically check if cart has expired (every minute)
  const checkCartExpiration = useCallback(() => {
    const order = LocalStorage.getItem(StorageKeys.ORDER);
    if (order?.timestamp && isCartExpired(order.timestamp)) {
      orderEmptyCart();
    }
  }, [orderEmptyCart]);

  useInterval(checkCartExpiration, 60 * 1000, [checkCartExpiration]);

  useEffectOnUpdates(() => {
    if (!auth.isAuthenticated) {
      emptyCart(setCartIdEditing, isDonationZeroAmountSelected, setIsDonationZeroAmountSelected);
    }
  }, [auth.user]);

  useEffect(() => {
    setCurrentOrderId(serverOrder.rbiOrderId);
  }, [serverOrder, setCurrentOrderId]);

  useEffectOnUpdates(() => {
    // Creating a map out of all valid offers in the CMS
    const cmsOffersMap = getCmsOffersMapByCmsId(loyaltyCmsOffers);
    appliedOffers.forEach(({ cartId, cmsId }) => {
      // If the applied offer is not in the cms offers map, should remove it
      const shouldRemoveCartEntry = !cmsId || !cmsOffersMap[cmsId];
      if (shouldRemoveCartEntry) {
        // Removing offer from cart will also remove it from applied offers
        removeFromCart({ cartId });
      }
    });
  }, [loyaltyCmsOffers]);

  const selectServiceMode = useCallback(
    newMode => {
      logRBIEvent({
        name: CustomEventNames.SELECT_SERVICE_MODE,
        type: EventTypes.Other,
        attributes: null,
      });
      setFireOrderIn(0);
      setTipSelection({
        percentAmount: TipAmounts.PERCENT_DEFAULT,
        dollarAmount: TipAmounts.DOLLAR_DEFAULT,
        otherAmount: 0,
        isOtherSelected: false,
      });
      setServiceMode(newMode);
      resetLastTimeStoreUpdated();
      return Promise.resolve(true);
    },
    [logRBIEvent, setTipSelection, setServiceMode, resetLastTimeStoreUpdated]
  );

  // Returns the currently editing cartEntry or undefined if the entry no longer exists
  const getCurrentCartEntry = useCallback(() => {
    return isSelectedOfferCartEntry({ cartId: cartIdEditing })
      ? selectedOfferCartEntry
      : cartEntries.find(entry => entry.cartId === cartIdEditing);
  }, [cartEntries, cartIdEditing, isSelectedOfferCartEntry, selectedOfferCartEntry]);

  // Changed this to a debounced effect because it was creating infinite loops in some scenarios.
  // e.g. Navigating through rewards and offers pages, going back to cart, then removing offers was causing this.
  useDebouncedEffect(
    () => {
      if (incentivesIds.size > 0) {
        validateCartEntries(cartEntries, appliedOffers, incentivesIds, removeFromCart);
      }
    },
    debounceTime,
    [appliedOffers, cartEntries, incentivesIds, removeFromCart]
  );

  const calculatedCartTotal = useCartTotal();

  const checkoutPriceLimit = useFlag('override-checkout-limit');
  const checkoutDeliveryPriceMinimum = useFlag('override-checkout-delivery-minimum');
  const checkoutCateringPriceLimit = useFlag('override-checkout-catering-limit');
  const checkoutCateringPriceMinimum = useFlag('override-checkout-catering-minimum');

  const [OrderLimitDialog, openOrderLimitDialog] = useDialogModal({
    modalAppearanceEventMessage: 'Order limit reached',
  });

  const alertOrderLimit = useCallback(() => {
    if (getCurrentPathname().startsWith(routes.cart)) {
      openOrderLimitDialog();
    }
  }, [getCurrentPathname, openOrderLimitDialog]);

  const queryOrder = useCallback(
    async rbiOrderId => {
      const { data: queryData, errors } = await apolloClient.query({
        fetchPolicy: 'network-only',
        query: GetOrderDocument,
        variables: {
          rbiOrderId,
        },
      });

      if (errors) {
        logger.error({ errors, message: 'Error querying order' });
        throw errors;
      }

      return queryData?.order;
    },
    [apolloClient]
  );

  // commit was being fired twice, which was firing onCommitSuccess twice
  // the lastPurchaseOrderRId checks if the same order is being committed twice
  // where we set .current = rbiOrderID is on the first run, so the second run will return early
  const lastPurchaseOrderId = useRef();
  const onCommitSuccess = useCallback(
    async remoteOrder => {
      // Always update the in-context server order so that a later call with the
      // post-commit polled order (from useOrderStatus) can overwrite the pre-commit
      // snapshot used to fire analytics, keeping downstream UI (order confirmation,
      // delivery tracking) consistent with the final committed status.
      setServerOrder(remoteOrder);

      if (lastPurchaseOrderId.current === remoteOrder.rbiOrderId) {
        return;
      }
      
      if (remoteOrder?.cart?.serviceMode === ServiceMode.CURBSIDE) {
        setCurbsidePickupOrderId('');
        setCurbsidePickupOrderTimePlaced('');
      }
      lastPurchaseOrderId.current = remoteOrder.rbiOrderId;
      emptyCart(setCartIdEditing, isDonationZeroAmountSelected, setIsDonationZeroAmountSelected);
      const cartEntriesData = offers.selectedOfferCartEntry
        ? [...remoteOrder.cart.cartEntries, offers.selectedOfferCartEntry]
        : remoteOrder.cart.cartEntries;
      const quotedFeeCents = remoteOrder.delivery?.quotedFeeCents || 0;
      const deliveryFeeCents = remoteOrder.delivery?.feeCents || 0;
      const deliveryFeeDiscountCents = remoteOrder.delivery?.feeDiscountCents || 0;
      const deliveryGeographicalFeeCents = remoteOrder.delivery?.geographicalFeeCents || 0;
      const deliveryServiceFeeCents = remoteOrder.delivery?.serviceFeeCents || 0;
      const deliverySmallCartFeeCents = remoteOrder.delivery?.smallCartFeeCents || 0;
      const baseDeliveryFeeCents = remoteOrder.delivery?.baseDeliveryFeeCents || 0;
      const deliverySurchargeFeeCents = remoteOrder.delivery?.deliverySurchargeFeeCents || 0;

      let giftCardBalance;
      if (remoteOrder.cart.payment?.cardType === 'GIFT_CARD') {
        const paymentMethods = await getPaymentMethods();
        const giftCardPaymentMethod = paymentMethods?.find(pm => pm.giftCard);
        giftCardBalance = giftCardPaymentMethod?.giftCard?.currentBalance;
      }

      logPurchase(cartEntriesData, store, serviceMode, remoteOrder, {
        currencyCode,
        quotedFeeCents,
        baseDeliveryFeeCents,
        totalDeliveryOrderFeesCents:
          baseDeliveryFeeCents +
          deliverySurchargeFeeCents +
          deliveryServiceFeeCents +
          deliverySmallCartFeeCents +
          deliveryGeographicalFeeCents -
          deliveryFeeDiscountCents,
        deliveryFeeCents:
          deliveryFeeCents -
          deliveryFeeDiscountCents -
          deliveryGeographicalFeeCents -
          deliveryServiceFeeCents -
          deliverySmallCartFeeCents,
        deliverySurchargeFeeCents,
        deliveryFeeDiscountCents,
        deliveryGeographicalFeeCents,
        deliveryServiceFeeCents,
        deliverySmallCartFeeCents,
        fireOrderInMinutes: Math.round(fireOrderIn / 60),
        giftCardBalance,
      });
      setShouldRefetchChallengeProgressUi(true);
      setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: evaluateChallengesQueryKeys.all,
        });
      }, 10);

      try {
        await Promise.all([
          selectedOffer ? onConfirmRedemption(selectedOffer) : Promise.resolve(),
          // refresh the user's payment methods stored
          // in state, including gift cards
          getPaymentMethods(),

          // refresh loyalty user points and recent transactions
          loyaltyUserId ? refetchLoyaltyUser() : Promise.resolve(),
          loyaltyUserId ? refetchLoyaltyUserTransaction() : Promise.resolve(),
          // refetch loyalty rewards
          dispatch(actions.loyalty.setShouldRefetchRewards(true)),
          queryClient.invalidateQueries({
            queryKey: loyaltyIncentivesQueryKeys.all,
            refetchType: 'all',
          }),
          queryClient.invalidateQueries({
            queryKey: rewardsTiersQueryKeys.loyaltyUser({ loyaltyId: loyaltyUserId }),
          }),
        ]);
      } catch (error) {
        logger.error({ error, message: 'Error after commit success' });
      }
    },
    [
      emptyCart,
      offers.selectedOfferCartEntry,
      logPurchase,
      store,
      serviceMode,
      currencyCode,
      fireOrderIn,
      selectedOffer,
      onConfirmRedemption,
      getPaymentMethods,
      refetchGetUserOrders,
      loyaltyUserId,
      refetchLoyaltyUser,
      refetchLoyaltyUserTransaction,
      dispatch,
    ]
  );

  const getAndRefreshServerOrder = useCallback(
    async id => {
      try {
        const order = await queryOrder(id);
        setServerOrder(order || {});
        return order;
      } catch (error) {
        logger.error({ error, message: 'Error refreshing order' });
      }
    },
    [queryOrder]
  );

  const fireOrderInXSeconds = useCallback(
    async ({ rbiOrderId, timeInSeconds }) => {
      const { data: updateOrderMutationData, errors } = await executeUpdateOrderMutation({
        variables: {
          input: {
            fireOrderIn: timeInSeconds,
            rbiOrderId,
          },
        },
      });

      if (errors) {
        logger.error({ errors, message: 'Error updating order.' });
        throw errors;
      }

      return getAndRefreshServerOrder(updateOrderMutationData?.updateOrder?.rbiOrderId);
    },
    [executeUpdateOrderMutation, getAndRefreshServerOrder]
  );

  const clearServerOrder = useCallback(() => {
    setServerOrder({});
  }, []);

  // make sure we persist everything!
  useEffect(() => {
    if (hasHydratedCartStore) {
      LocalStorage.setItem(StorageKeys.ORDER, {
        cartEntries,
        cartIdEditing,
        cartVersion: CART_VERSION,
        cateringPickupDateTime,
        deliveryAddress,
        deliveryInstructions,
        quoteId,
        orderPhoneNumber,
        curbsidePickupOrderId,
        curbsidePickupOrderTimePlaced,
        timestamp: Date.now(),
      });
    }
  }, [
    hasHydratedCartStore,
    cartEntries,
    cateringPickupDateTime,
    serviceMode,
    deliveryInstructions,
    orderPhoneNumber,
    deliveryAddress,
    curbsidePickupOrderId,
    curbsidePickupOrderTimePlaced,
    cartIdEditing,
    CART_VERSION,
    quoteId,
  ]);

  // Configure the logger to hold some information
  useEffect(() => {
    const storePosVendor = store?.pos?.vendor?.toLowerCase();
    const vendorKey = getPosVendorFromStore(store, serviceMode);

    // recursively remove unused vendorConfigs / null entries and don't mutate the cart
    let cartEntriesStringified = 'UNKNOWN';
    try {
      cartEntriesStringified = JSON.stringify(
        cartEntries,
        (key, value) => {
          if (key === 'image') {
            return undefined; // strip image info (not super useful)
          }

          if (key !== 'vendorConfigs' || !value) {
            return value; // don't touch anything other than vendor config
          }

          if (!storePosVendor) {
            return undefined; // if there isn't a store set just don't return vendor configs
          }

          const vendorConfigObj = value[vendorKey];

          const nullFreeVendorConfig = vendorConfigObj
            ? Object.entries(vendorConfigObj).reduce(
                (a, [k, v]) => (v == null ? a : ((a[k] = v), a)),
                {}
              )
            : vendorConfigObj;
          return { [vendorKey]: nullFreeVendorConfig };
        },
        ' '
      );
    } catch (error) {
      cartEntriesStringified = `UNKNOWN due to ERROR: ${error?.toString()}`;
    }

    const extras = {
      cartEntries: cartEntriesStringified,
      serviceMode,
      storePosVendor,
    };

    if (serverOrder.rbiOrderId) {
      DatadogLogger.addContext('transaction_id', serverOrder.rbiOrderId);
    }

    addLoggingContext({ ...extras, transactionId: serverOrder.rbiOrderId });
  }, [cartEntries, serverOrder.rbiOrderId, serviceMode, store]);

  // show error cart price limit modal
  const isCatering = isCateringOrder(serviceMode);
  const calculateCartTotal = useCallback(() => calculatedCartTotal, [calculatedCartTotal]);

  useAlertOrderLimit({
    calculateCartTotal,
    checkoutPriceLimit,
    checkoutCateringPriceLimit,
    alertOrderLimit,
    isCatering,
    setCartPriceLimitExceeded,
  });

  useAlertOrderCateringMinimum({
    calculateCartTotal,
    checkoutCateringPriceMinimum,
    isCatering,
    setCartCateringPriceTooLow,
  });

  const { handleReorder, loadingEnrichCartEntries } = useHandleReorder({
    setPendingReorder,
    setReordering,
    storeHasSelection: isStoreAvailable,
    setUnavailableCartEntries,
    setReorderedOrderId,
  });

  const offerCartEntry = offers.selectedOfferCartEntry || null;
  const cartPreviewEntries = useMemo(
    () => (offerCartEntry ? [offerCartEntry, ...cartEntries] : cartEntries),
    [offerCartEntry, cartEntries]
  );
  const isCartEmpty = !cartPreviewEntries.length;
  const canUserCheckout = !isCartEmpty && !cartPriceLimitExceeded;
  const numCartPreviewEntries = (cartPreviewEntries || []).reduce((accum, entry) => {
    if (entry.isDonation) {
      return accum;
    }

    return accum + (entry.quantity ?? 1);
  }, 0);

  const selectStore = useCallback(
    async (newStore, callback, requestedServiceMode) => {
      setFetchingPosData(true);
      const updateCallback = async () => {
        await selectNewStore({
          sanityStore: newStore,
          hasCartItems: !isCartEmpty,
          callback,
          requestedServiceMode,
          selectedOffer,
        });
        setFetchingPosData(false);
      };

      if (isDelivery) {
        await updateCallback();
      } else {
        updateUserStoreWithCallback(newStore, updateCallback);
      }
    },
    [isCartEmpty, isDelivery, selectNewStore, selectedOffer, updateUserStoreWithCallback]
  );

  const [shouldHandleReorder, setShouldHandleReorder] = useState(false);
  useEffect(() => {
    if (!shouldHandleReorder) {
      return;
    }
    // we have to wait for pricing to be complete from selecting a new store before we redirect
    // we also only want to reorder if we haven't already, pendingReorder gets set to null when we are done reordering
    if (isStoreAvailable && pendingReorder) {
      // Pop store locator modal while reordering
      setShouldHandleReorder(false);
      handleReorder(pendingReorder);
    }
  }, [
    handleReorder,
    isStoreAvailable,
    pendingReorder,
    reordering,
    setReordering,
    shouldHandleReorder,
  ]);
  const evaluateEligibleItemsAbortController = useRef(null);
  useDebouncedEffect(
    async function runEligibleItemsEvaluation() {
      // Abort any previous request
      if (evaluateEligibleItemsAbortController.current) {
        evaluateEligibleItemsAbortController.current.abort();
      }

      if (!loyaltyUserId) {
        return;
      }
      if (appliedLoyaltyOffers.length) {
        evaluateEligibleItemsAbortController.current = new AbortController();
        evaluateEligibleItems({
          loyaltyId: loyaltyUserId,
          appliedLoyaltyOffers,
          cartEntries,
          appliedRewards,
          selectedOfferSelections,
          signal: evaluateEligibleItemsAbortController.current.signal,
        });
      }
    },
    debounceTime,
    [
      loyaltyUserId,
      appliedLoyaltyOffers,
      evaluateEligibleItems,
      cartEntries,
      appliedRewards,
      selectedOfferSelections,
    ]
  );

  const value = useMemo(
    () => ({
      // state
      cartEntries,
      numCartPreviewEntries,
      unavailableCartEntries,
      serviceMode,
      serverOrder,
      cartPriceLimitExceeded,
      cartCateringPriceTooLow,
      cartHasRewardEligibleItem,
      // Catering
      cateringPickupDateTime,
      setOrderCateringPickupDateTime,
      // Cart
      checkoutPriceLimit,
      checkoutDeliveryPriceMinimum,
      checkoutCateringPriceLimit,
      checkoutCateringPriceMinimum,
      alertOrderLimit,
      removeFromCart,
      removeAllFromCart,
      setUnavailableCartEntries,
      cartIdEditing,
      getCurrentCartEntry,
      addItemToCart,
      upsertCart,
      updateCartEntry,
      updateQuantity,
      calculatedCartTotal,
      emptyCart: orderEmptyCart,
      tipAmount,
      setTipAmount,
      updateTipAmount,
      tipSelection,
      setTipSelection,
      shouldShowTipPercentage,
      isCartEmpty,
      cartPreviewEntries,
      canUserCheckout,
      isCartEntryInSwaps,
      // Order
      selectServiceMode,
      clearServerOrder,
      selectStore,
      fetchingPosData,
      clearCartStoreServiceModeTimeout: () => {}, // unused
      deliveryAddress,
      setDeliveryAddress,
      deliveryInstructions,
      setDeliveryInstructions,
      orderPhoneNumber,
      setOrderPhoneNumber,
      isCatering,
      isDelivery,
      fireOrderIn,
      setFireOrderIn,
      getCurrentFireOrderIn,
      onCommitSuccess,
      tipPercentThresholdCents,
      curbsidePickupOrderId,
      updateShouldSaveDeliveryAddress,
      setCurbsidePickupOrderId,
      curbsidePickupOrderTimePlaced,
      setCurbsidePickupOrderTimePlaced,
      setQuoteId,
      quoteId,
      // Server Interactions
      query: getAndRefreshServerOrder,
      fireOrderInXSeconds,
      reorder: {
        handleReorder,
        reordering,
        pendingReorder,
        setReordering,
        setPendingReorder,
        reorderedOrderId,
        setShouldHandleReorder,
        loadingEnrichCartEntries,
      },
      recent: {
        setPendingRecentItem,
        pendingRecentItem,
      },
      setCartIdEditing,
      isDonationZeroAmountSelected,
      setIsDonationZeroAmountSelected,
      selectedPickupTip,
      setSelectedPickupTip,
      otherPickupTip,
      pickUpTipsCents,
      pickUpTipsInputError,
      setOtherPickupTip,
      selectedDonationId,
      setSelectedDonationId,
      addItemToCartLoading,
      setAddItemToCartLoading,
    }),
    [
      cartEntries,
      numCartPreviewEntries,
      CART_VERSION,
      unavailableCartEntries,
      serviceMode,
      serverOrder,
      cartPriceLimitExceeded,
      cartCateringPriceTooLow,
      cartHasRewardEligibleItem,
      cateringPickupDateTime,
      checkoutPriceLimit,
      checkoutDeliveryPriceMinimum,
      checkoutCateringPriceLimit,
      checkoutCateringPriceMinimum,
      alertOrderLimit,
      removeFromCart,
      removeAllFromCart,
      setUnavailableCartEntries,
      cartIdEditing,
      setCartIdEditing,
      getCurrentCartEntry,
      addItemToCart,
      upsertCart,
      updateCartEntry,
      updateQuantity,
      calculatedCartTotal,
      orderEmptyCart,
      tipAmount,
      setTipAmount,
      updateTipAmount,
      tipSelection,
      setTipSelection,
      shouldShowTipPercentage,
      isCartEmpty,
      cartPreviewEntries,
      canUserCheckout,
      isCartEntryInSwaps,
      selectServiceMode,
      clearServerOrder,
      selectStore,
      fetchingPosData,
      deliveryAddress,
      deliveryInstructions,
      orderPhoneNumber,
      isCatering,
      isDelivery,
      fireOrderIn,
      getCurrentFireOrderIn,
      onCommitSuccess,
      tipPercentThresholdCents,
      curbsidePickupOrderId,
      updateShouldSaveDeliveryAddress,
      curbsidePickupOrderTimePlaced,
      quoteId,
      getAndRefreshServerOrder,
      fireOrderInXSeconds,
      handleReorder,
      reordering,
      pendingReorder,
      reorderedOrderId,
      loadingEnrichCartEntries,
      pendingRecentItem,
      isDonationZeroAmountSelected,
      setIsDonationZeroAmountSelected,
      selectedPickupTip,
      setSelectedPickupTip,
      otherPickupTip,
      pickUpTipsCents,
      pickUpTipsInputError,
      setOtherPickupTip,
      selectedDonationId,
      setSelectedDonationId,
      addItemToCartLoading,
      setAddItemToCartLoading,
    ]
  );

  return (
    <OrderContext.Provider value={value}>
      {props.children}

      <EvaluateChallengesPrefetch />

      <OrderLimitDialog
        body={orderLimitMessage(checkoutPriceLimit, checkoutCateringPriceLimit, isCatering)}
        heading={formatMessage({ id: 'orderLimitHeading' })}
      />
    </OrderContext.Provider>
  );
}

export default OrderContext.Consumer;
